var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { useCronoSkeleton } from '../../Boost/CronoSkeleton';
import { Manager, Reference, Popper } from 'react-popper';
import Icon from '../../Styles/Icons/Icon';
import Typography from '../../Styles/Typography/Typography';
import Avatar from '../../Elements/Avatar/Avatar';
import Spacer from '../../Layout/Spacer/Spacer';
import Box from '../../Layout/Flex/Box';
const DropDownContainer = styled.div `
  font-family: ${props => props.theme.fonts.fontFamily};
  box-sizing: border-box;
  position: relative;
  border-radius: ${props => props.theme.borders.inputsBorderRadius};
  display: ${props => (props.component ? 'inline-flex' : 'inline-block')};
  margin: ${props => props.noMargin || props.component ? '0px 0px 0px 0px' : props.theme.paddingsMargins.themeMargin};
  width: ${props => (props.fullWidth ? '100%' : 'auto')};
`;
const DropDownLabel = styled.div `
  box-sizing: border-box;
  font-family: ${props => props.theme.fonts.fontFamily};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${props => props.theme.utils.getColor(props.color)};
  border: ${props => props.isSelect || props.withBorder
    ? props.theme.utils.customBorders(props.borderColor ? props.borderColor : '#E0E0E0')
    : 'none'};
  border-radius: ${props => props.theme.borders.inputsBorderRadius};
  outline: none;
  padding: ${props => props.withBorder
    ? props.theme.paddingsMargins.themeMenuPadding
    : props.isSelect ? '0.2rem' : '0'};
  ${props => props.isSelect &&
    css `
      padding-left: 0.6rem
  `};
  background-color: inherit;
  font-family: inherit;
  margin: 0;
`;
const DropDownContent = styled.div `
  font-family: ${props => props.theme.fonts.fontFamily};
  border-radius: ${props => props.isSelect ? '5px' : props.theme.borders.inputsBorderRadius};
  padding: ${props => props.noPadding ? '0rem 0rem 0rem 0rem' : props.theme.paddingsMargins.themePadding};
  background-color:  ${props => props.invisibleDropDown ? 'transparent' : props.backgroundColor ? props.theme.utils.getColor(props.backgroundColor) : 'white'};
  width: ${props => (props.fullWidth ? '97%' : 'max-content')};
  box-shadow: ${props => props.invisibleDropDown ? '0' : props.theme.boxes.boxShadow};
  z-index: 999;
  height: ${props => props.height};
  overflow: ${props => props.overflow};
`;
const DropDown = (_a) => {
    var { color = 'main', text, labelSize = 'article', icon, iconSize, avatar, avatarSize = 'subArticle', component, height = 'auto', overflow = 'auto', placement = 'bottom', invisibleDropDown = false, withBorder = false, isMenuOpen = () => { }, children, hideDropdown = false, noMargin = false, noPadding = false, isSelect = false, closeOnClick = false, onlyClick = false, borderColor, fullWidth = false, isLoading = false, positionFixed = false, backgroundColor = 'white', excludeMeFromLoadingContext = false, setIsOpen = false, skeleton } = _a, props = __rest(_a, ["color", "text", "labelSize", "icon", "iconSize", "avatar", "avatarSize", "component", "height", "overflow", "placement", "invisibleDropDown", "withBorder", "isMenuOpen", "children", "hideDropdown", "noMargin", "noPadding", "isSelect", "closeOnClick", "onlyClick", "borderColor", "fullWidth", "isLoading", "positionFixed", "backgroundColor", "excludeMeFromLoadingContext", "setIsOpen", "skeleton"]);
    const [showMenu, setShowMenu] = useState(false);
    const dropdownRef = useRef(null);
    const theme = useTheme();
    const { loadingContext } = useCronoSkeleton();
    const menuHandler = () => {
        if (!hideDropdown) {
            setShowMenu(!showMenu);
            isMenuOpen(!showMenu);
        }
    };
    useEffect(() => {
        setShowMenu(setIsOpen);
    }, [setIsOpen]);
    useEffect(() => {
        if (hideDropdown) {
            setShowMenu(false);
            isMenuOpen(false);
        }
    }, [hideDropdown]);
    useEffect(() => {
        if (onlyClick && showMenu) {
            function handler(event) {
                var _a;
                if (!((_a = dropdownRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target))) {
                    setShowMenu(false);
                    isMenuOpen(false);
                }
            }
            window.addEventListener('click', handler);
            return () => window.removeEventListener('click', handler);
        }
    }, [isMenuOpen, onlyClick, showMenu]);
    const mouseOut = () => {
        if (showMenu && !onlyClick)
            menuHandler();
    };
    const mouseIn = () => {
        if (!showMenu && !onlyClick)
            menuHandler();
    };
    const mouseClicked = () => {
        if (closeOnClick && showMenu)
            menuHandler();
    };
    return (_jsx(DropDownContainer, Object.assign({ fullWidth: fullWidth, noMargin: noMargin, component: component, onMouseLeave: mouseOut, ref: dropdownRef }, props, { children: _jsxs(Manager, { children: [_jsx(Reference, { children: ({ ref }) => _jsxs(DropDownLabel, Object.assign({ onMouseEnter: mouseIn, onClick: () => menuHandler(), color: color, withBorder: withBorder, ref: ref, isSelect: isSelect, borderColor: showMenu ? theme.utils.getColorLight(color) : borderColor }, { children: [_jsxs(Box, Object.assign({ alignItems: "center" }, { children: [component || (avatar
                                        ? _jsx(Avatar, { noMargin: !!noMargin, avatar: avatar, size: avatarSize, isLoading: isLoading || (loadingContext && excludeMeFromLoadingContext === false) })
                                        : icon &&
                                            _jsx(Icon, { size: iconSize || labelSize, icon: icon, color: color, isLoading: isLoading || (loadingContext && excludeMeFromLoadingContext === false) })), _jsx(Typography, Object.assign({ color: color, size: labelSize, style: { userSelect: 'none' }, isLoading: isLoading || (loadingContext && excludeMeFromLoadingContext === false), skeleton: Object.assign({ width: text ? '100px' : '0px' }, skeleton) }, { children: text }))] })), isSelect &&
                                _jsxs(Box, { children: [_jsx(Spacer, { half: true }), _jsx(Typography, Object.assign({ color: 'grey', style: { position: 'relative', top: '2px' } }, { children: "|" })), _jsx(Icon, { size: labelSize, icon: "arrowDown", noMargin: !!noMargin, color: showMenu ? 'dark' : 'grey' })] })] })) }), _jsx(Popper, Object.assign({ placement: placement, modifiers: [{ name: 'preventOverflow' }], strategy: 'fixed' }, { children: ({ ref, style, placement }) => !(isLoading || (loadingContext && excludeMeFromLoadingContext === false)) && showMenu &&
                        _jsx(DropDownContent, Object.assign({ fullWidth: fullWidth, ref: ref, invisibleDropDown: invisibleDropDown, backgroundColor: backgroundColor, style: style, "data-placement": placement, height: height, overflow: overflow, noPadding: noPadding, onClick: mouseClicked, isSelect: isSelect }, { children: children })) }))] }) })));
};
export default DropDown;
